import { Title } from '@solidjs/meta'
import { useSearchParams } from '@solidjs/router'
import { type Component, createEffect, createMemo, createSignal, For, Show } from 'solid-js'
import { createStore } from 'solid-js/store'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Card from '~/components/Card'
import Chip from '~/components/Chip'
import Icon from '~/components/Icon'
import InfiniteList from '~/components/InfiniteList'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import SwipeAction from '~/components/SwipeAction'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useDebouncedSignal } from '~/hooks/useDebouncedSignal'
import { useIsMobile } from '~/hooks/useIsMobile'
import { usePermissions } from '~/hooks/usePermissions'
import { useServerTable } from '~/hooks/useServerTable'
import { executionLabel, relativeTime, workflowStatusColor, workflowStatusIcon, workflowStatusLabel } from '~/layers/ui/admin/automatizaciones/workflowMeta'
import { type Path, useNavigate } from '~/router'
import { workflowService } from '~/services/workflow.service'
import type { WorkflowGraphType, WorkflowType } from '~/types/workflow'
import { resolveError } from '~/utils/error'
import { P } from '~/utils/permissions'

/**
 * Listado de workflows.
 *
 * Sigue el mismo patrón que el resto de listados del panel (pedidos, clientes…):
 * filtro de estado en la URL, búsqueda debounced, Table server-side con
 * selección y acciones, y vista de tarjetas + InfiniteList en móvil.
 */
const AutomatizacionesPage: Component = () => {
  const toast = useToast()
  const alert = useAlert()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const isMobile = useIsMobile()
  const [searchParams, setSearchParams] = useSearchParams()

  const sanitizeStatus = (value: string | undefined): string => (value === 'active' || value === 'inactive' || value === 'archived' ? value : '')

  const filterStatus = createMemo(() => sanitizeStatus(searchParams.status as string | undefined))
  const [_search, debouncedSearch, setSearch] = useDebouncedSignal('')

  // El filtro vive en la URL: compartir el enlace de "activos" comparte el filtro.
  const handleFilterChange = (value: string) => {
    const next: Record<string, string | string[] | undefined> = { ...searchParams }
    next.status = value || undefined
    setSearchParams(next, { replace: true })
  }

  const buildParams = (): Record<string, string | number | undefined> => {
    const q = debouncedSearch().trim()
    const p: Record<string, string | number | undefined> = {}
    if (q) p.q = q
    if (filterStatus()) p.status = filterStatus()
    return p
  }

  const table = useServerTable<WorkflowType>({
    fetcher: params => workflowService.list(params as Parameters<typeof workflowService.list>[0]),
    params: buildParams
  })

  // ── Listado móvil: se acumula aparte del de escritorio ─────────────────────
  const [mobileItems, setMobileItems] = createStore<WorkflowType[]>([])
  const [mobileLoading, setMobileLoading] = createSignal(false)
  const [mobileTotal, setMobileTotal] = createSignal(0)
  const hasMore = () => mobileItems.length < mobileTotal()

  const mobileFetch = async (reset: boolean) => {
    if (!reset && mobileLoading()) return
    setMobileLoading(true)
    try {
      const page = reset ? 1 : Math.floor(mobileItems.length / table.perPage()) + 1
      const res = await workflowService.list({ ...buildParams(), page, limit: table.perPage() })
      setMobileTotal(res.meta.total)
      setMobileItems(reset ? res.items : prev => [...prev, ...res.items])
    } catch {
      // Silencioso: el sentinela del InfiniteList reintenta al volver a entrar.
    } finally {
      setMobileLoading(false)
    }
  }

  const [filtersKey, setFiltersKey] = createSignal('')
  createEffect(() => {
    if (!isMobile()) return
    const key = JSON.stringify([filterStatus(), debouncedSearch()])
    if (key === filtersKey()) return
    setFiltersKey(key)
    void mobileFetch(true)
  })
  const loadMore = () => {
    if (!hasMore() || mobileLoading()) return
    void mobileFetch(false)
  }

  const [showTpl, setShowTpl] = createSignal(false)
  const [templates, setTemplates] = createSignal<Array<{ title: string; description?: string; graph?: WorkflowGraphType }>>([])

  const handleCreate = async (graph?: WorkflowGraphType) => {
    try {
      const creado = await workflowService.create({ name: 'Nuevo flujo', graph })
      navigate(`/admin/automatizaciones/${creado.id}` as Path)
    } catch (e) {
      toast.addToast({ title: resolveError(e), type: 'danger' })
    }
  }

  const duplicate = async (r: WorkflowType) => {
    try {
      const draft = await workflowService.getDraft(r.id)
      const creado = await workflowService.create({ name: `${r.name} (copia)`, graph: draft.graph })
      toast.addToast({ title: 'Flujo duplicado', type: 'success' })
      await table.refetch()
      if (isMobile()) await mobileFetch(true)
      navigate(`/admin/automatizaciones/${creado.id}` as Path)
    } catch (e) {
      toast.addToast({ title: resolveError(e), type: 'danger' })
    }
  }

  const toggleStatus = async (r: WorkflowType) => {
    try {
      if (r.status === 'active') await workflowService.deactivate(r.id)
      else await workflowService.activate(r.id)
      toast.addToast({ title: r.status === 'active' ? 'Flujo desactivado' : 'Flujo activado', type: 'success' })
      await table.refetch()
      if (isMobile()) await mobileFetch(true)
    } catch (e) {
      toast.addToast({ title: resolveError(e), type: 'danger' })
    }
  }

  const removeWorkflow = async (r: WorkflowType) => {
    const ok = await alert.confirmDelete({
      title: `Eliminar "${r.name}"`,
      message: 'El flujo y todo su historial de versiones y ejecuciones se eliminarán. Esta acción no se puede deshacer.'
    })
    if (!ok) return
    try {
      await workflowService.delete(r.id)
      toast.addToast({ title: 'Flujo eliminado', type: 'success' })
      await table.refetch()
      if (isMobile()) await mobileFetch(true)
    } catch (e) {
      toast.addToast({ title: resolveError(e), type: 'danger' })
    }
  }

  const openTemplates = async () => {
    try {
      const { data } = await (await import('~/services/api')).default.get('/workflows/templates')
      setTemplates(data.data ?? [])
    } catch {
      setTemplates([])
    }
    setShowTpl(true)
  }

  const filterOptions = createMemo(() => [
    { label: 'Todos los estados', value: '' },
    { label: 'Activos', value: 'active', color: 'success' as const },
    { label: 'Inactivos', value: 'inactive', color: 'warning' as const },
    { label: 'Archivados', value: 'archived', color: 'secondary' as const }
  ])

  /** Estado de la última ejecución: icono + etiqueta + tiempo relativo. */
  const lastExecutionCell = (r: WorkflowType) => {
    const ex = r.lastExecution
    if (!ex) return <span class="text-foreground-subtle">—</span>
    return (
      <button
        type="button"
        class="group flex items-center gap-1.5 text-sm text-foreground-muted hover:text-primary"
        title={`${ex.publicId} · ${new Date(ex.createdAt).toLocaleString('es-CO')}${ex.durationMs != null ? ` · ${ex.durationMs}ms` : ''}`}
        onClick={e => {
          e.stopPropagation()
          navigate(`/admin/automatizaciones/${r.id}?ejecucion=${ex.publicId}` as Path)
        }}
      >
        <span
          class="h-2 w-2 shrink-0 rounded-full bg-current text-{executionColor(ex.status)}"
          classList={{
            'text-success': ex.status === 'succeeded',
            'text-danger': ex.status === 'failed',
            'text-info': ex.status === 'running' || ex.status === 'queued',
            'text-warning': ex.status === 'waiting'
          }}
        />
        <span class="text-xs">
          {executionLabel(ex.status)}
          <Show when={ex.durationMs != null}> · {ex.durationMs}ms</Show>
        </span>
        <span class="text-xs text-foreground-subtle">{relativeTime(ex.createdAt)}</span>
      </button>
    )
  }

  const columns = [
    {
      header: 'Nombre',
      accessor: (r: WorkflowType) => (
        <div class="min-w-56">
          <div class="flex items-center gap-2">
            <button class="truncate text-left font-medium text-primary hover:underline" onClick={() => navigate(`/admin/automatizaciones/${r.id}` as Path)}>
              {r.name}
            </button>
          </div>
          <div class="truncate text-xs text-foreground-subtle">
            /{r.slug}
            {r.description ? ` · ${r.description}` : ''}
          </div>
        </div>
      ),
      sticky: 'left' as const,
      minWidth: 240
    },
    {
      header: 'Estado',
      accessor: (r: WorkflowType) => (
        <Chip size="sm" variant="soft" color={workflowStatusColor(r.status)} icon={workflowStatusIcon(r.status)} iconSize={13}>
          {workflowStatusLabel(r.status)}
        </Chip>
      )
    },
    { header: 'Última ejecución', accessor: (r: WorkflowType) => lastExecutionCell(r), minWidth: 180 },
    { header: 'Versión', accessor: (r: WorkflowType) => <span class="font-mono text-xs text-foreground-muted">v{r.revision}</span> },
    { header: 'Propietario', accessor: (r: WorkflowType) => <span class="text-foreground-muted">{r.ownerName ?? '—'}</span> },
    { header: 'Modificado', accessor: (r: WorkflowType) => <span class="text-foreground-muted">{relativeTime(r.updatedAt)}</span> }
  ]

  return (
    <>
      <Title>Automatizaciones | Florez Jaramillo</Title>

      <Show when={table.error()}>
        <div class="rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3 text-sm text-danger mb-4">{resolveError(table.error())}</div>
      </Show>

      <Show when={!isMobile()}>
        <Table<WorkflowType>
          title="Workflows"
          subtitle="Diseña, prueba y publica procesos automáticos conectando tus servicios, datos y agentes de IA."
          data={table.data()}
          columns={columns}
          keyField="id"
          loading={table.loading()}
          searchable
          searchPlaceholder="Buscar por nombre o slug..."
          onSearch={setSearch}
          serverSide
          total={table.total()}
          page={table.page()}
          perPage={table.perPage()}
          onPageChange={table.onPageChange}
          onPerPageChange={table.onPerPageChange}
          showFilters
          headSticky
          filterField="status"
          activeFilter={filterStatus()}
          onFilterChange={handleFilterChange}
          filterOptions={filterOptions()}
          emptyMessage="No hay workflows con estos filtros"
          onRowClick={r => navigate(`/admin/automatizaciones/${r.id}` as Path)}
          renderTopHeader={
            <div class="flex flex-wrap items-center gap-3 p-4">
              <div class="min-w-0 flex-1">
                <h2 class="font-semibold text-lg text-foreground">Tus workflows</h2>
                <p class="text-sm text-foreground-subtle">{table.total()} en total</p>
              </div>
              <div class="flex flex-wrap gap-2">
                <Button variant="soft" icon="material-symbols-light:dashboard-customize-outline" onClick={openTemplates}>
                  Plantillas
                </Button>
                <Button variant="soft" icon="material-symbols-light:key-outline" onClick={() => navigate('/admin/automatizaciones/credenciales' as Path)}>
                  Credenciales
                </Button>
                <Show when={can(P.workflows.create)}>
                  <Button color="primary" icon="material-symbols-light:add" onClick={() => handleCreate()}>
                    Crear workflow
                  </Button>
                </Show>
              </div>
            </div>
          }
          actions={r => (
            <ButtonGroup size="sm" variant="soft">
              <Show when={can(P.workflows.update)}>
                <Button
                  color={r.status === 'active' ? 'warning' : 'success'}
                  icon={r.status === 'active' ? 'material-symbols-light:pause-rounded' : 'material-symbols-light:play-arrow-rounded'}
                  tooltip={r.status === 'active' ? 'Desactivar' : 'Activar'}
                  onClick={() => toggleStatus(r)}
                />
              </Show>
              <Show when={can(P.workflows.create)}>
                <Button color="info" icon="material-symbols-light:content-copy" tooltip="Duplicar" onClick={() => duplicate(r)} />
              </Show>
              <Show when={can(P.workflows.delete)}>
                <Button color="danger" icon="material-symbols-light:delete-outline" tooltip="Eliminar" onClick={() => removeWorkflow(r)} />
              </Show>
            </ButtonGroup>
          )}
        />
      </Show>

      <Show when={isMobile()}>
        <div class="mb-3 flex items-center justify-between gap-2">
          <div>
            <h1 class="text-xl font-bold text-foreground">Workflows</h1>
            <p class="text-sm text-foreground-subtle">{table.total()} en total</p>
          </div>
          <div class="flex gap-2">
            <Button size="sm" variant="soft" icon="material-symbols-light:dashboard-customize-outline" aria-label="Plantillas" tooltip="Plantillas" onClick={openTemplates} />
            <Show when={can(P.workflows.create)}>
              <Button size="sm" color="primary" icon="material-symbols-light:add" onClick={() => handleCreate()}>
                Crear
              </Button>
            </Show>
          </div>
        </div>
        <div class="mb-3 flex gap-2">
          <Input
            placeholder="Buscar por nombre o slug..."
            value={_search()}
            onInput={(e: InputEvent) => setSearch((e.currentTarget as HTMLInputElement).value)}
            startIcon="material-symbols-light:search"
            class="flex-1"
            aria-label="Buscar workflow"
          />
        </div>
        <InfiniteList<WorkflowType>
          items={mobileItems}
          keyField={w => w.id}
          hasMore={hasMore()}
          loading={mobileLoading()}
          onLoadMore={loadMore}
          onRefresh={async () => {
            await mobileFetch(true)
          }}
          emptyMessage={
            <div class="px-5 py-14 text-center">
              <IconCenter icon="material-symbols-light:account-tree-outline" />
              <h2 class="mt-3 font-semibold">No tienes workflows todavía</h2>
              <p class="mx-auto mt-1 max-w-xs text-sm text-foreground-subtle">Crea tu primer flujo para automatizar tareas repetitivas.</p>
              <Show when={can(P.workflows.create)}>
                <Button class="mt-4" size="sm" color="primary" icon="material-symbols-light:add" onClick={() => handleCreate()}>
                  Crear workflow
                </Button>
              </Show>
            </div>
          }
          endMessage={<span>Has llegado al final de tus workflows</span>}
          class="space-y-3"
          renderItem={r => (
            <SwipeAction
              itemId={r.id}
              rememberPosition
              onClick={() => navigate(`/admin/automatizaciones/${r.id}` as Path)}
              rightOptions={[
                ...(can(P.workflows.create)
                  ? [
                      {
                        icon: 'material-symbols-light:content-copy',
                        label: 'Duplicar',
                        color: 'info' as const,
                        action: () => duplicate(r)
                      }
                    ]
                  : []),
                ...(can(P.workflows.delete)
                  ? [
                      {
                        icon: 'material-symbols-light:delete-outline',
                        label: 'Eliminar',
                        color: 'danger' as const,
                        action: () => removeWorkflow(r)
                      }
                    ]
                  : [])
              ]}
            >
              <Card class="!rounded-lg">
                <Card.Body>
                  <div class="flex items-start gap-3">
                    <span class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                      <Icon icon="material-symbols-light:account-tree-outline" size={20} />
                    </span>
                    <div class="min-w-0 flex-1">
                      <p class="truncate font-medium text-foreground">{r.name}</p>
                      <p class="truncate text-xs text-foreground-subtle">/{r.slug}</p>
                      <div class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-subtle">
                        <Chip size="xs" variant="soft" color={workflowStatusColor(r.status)} icon={workflowStatusIcon(r.status)} iconSize={12}>
                          {workflowStatusLabel(r.status)}
                        </Chip>
                        <span class="font-mono">v{r.revision}</span>
                        <Show when={r.ownerName}>
                          <span>{r.ownerName}</span>
                        </Show>
                        <span>{relativeTime(r.updatedAt)}</span>
                      </div>
                    </div>
                    <Icon icon="material-symbols-light:chevron-right" size={20} class="shrink-0 text-foreground-subtle" />
                  </div>
                </Card.Body>
              </Card>
            </SwipeAction>
          )}
        />
      </Show>

      <Modal isOpen={showTpl()} onClose={() => setShowTpl(false)} size="lg">
        <Modal.Header>
          <h2 class="font-semibold">Plantillas</h2>
        </Modal.Header>
        <Modal.Body>
          <div class="grid gap-2">
            <Show when={!templates().length}>
              <p class="text-sm text-foreground-subtle">Sin plantillas o error cargando</p>
            </Show>
            <For each={templates()}>
              {tpl => (
                <button
                  type="button"
                  class="text-left rounded-lg border border-border p-3 hover:border-primary"
                  onClick={() => {
                    setShowTpl(false)
                    void handleCreate(tpl.graph)
                  }}
                >
                  <p class="font-medium text-sm">{tpl.title}</p>
                  <p class="text-xs text-foreground-subtle">{tpl.description}</p>
                </button>
              )}
            </For>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end">
            <Button variant="soft" onClick={() => setShowTpl(false)}>
              Cerrar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <alert.AlertComponent />
    </>
  )
}

/** Icono centrado para el empty state móvil. */
function IconCenter(props: { icon: string }) {
  return <Icon icon={props.icon} size={42} class="mx-auto text-foreground-subtle" />
}

export default AutomatizacionesPage
